import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '../services/api';

function ScoreRing({ label, score, color, icon, delay = 0 }) {
  const [visible, setVisible] = useState(false);
  const [animated, setAnimated] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), delay);
    const t2 = setTimeout(() => setAnimated(score), delay + 200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [delay, score]);

  const r = 52, circ = 2 * Math.PI * r;
  const progress = (animated / 100) * circ;
  const grade = score >= 80 ? 'A' : score >= 60 ? 'B' : score >= 40 ? 'C' : 'D';

  return (
    <div className={`bg-slate-800/60 border rounded-3xl p-6 text-center transition-all duration-700 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
      style={{ borderColor: `${color}40` }}>
      <div className="relative w-36 h-36 mx-auto mb-4">
        <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
          <circle cx="60" cy="60" r={r} fill="none" stroke="#1e293b" strokeWidth="10"/>
          <circle cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth="10"
            strokeLinecap="round" strokeDasharray={`${progress} ${circ}`}
            style={{ transition: 'stroke-dasharray 1.4s cubic-bezier(0.4,0,0.2,1)', filter: `drop-shadow(0 0 8px ${color}80)` }}/>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-3xl">{icon}</div>
          <div className="text-2xl font-black text-white">{Math.round(animated)}</div>
          <div className="text-xs font-bold px-2 py-0.5 rounded-full mt-0.5" style={{ background: `${color}25`, color }}>{grade}</div>
        </div>
      </div>
      <div className="font-bold text-white text-sm">{label}</div>
      <div className="text-xs text-slate-500 mt-1">out of 100</div>
    </div>
  );
}

function PatternCard({ icon, label, value, unit, color, desc, delay = 0 }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), delay); return () => clearTimeout(t); }, [delay]);
  return (
    <div className={`bg-slate-800/60 border rounded-2xl p-5 transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
      style={{ borderColor: `${color}35` }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center text-xl" style={{ background: `${color}20` }}>{icon}</div>
          <div>
            <div className="text-sm font-semibold text-white">{label}</div>
            <div className="text-xs text-slate-500">{desc}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-black" style={{ color }}>{value}</div>
          <div className="text-xs text-slate-500">{unit}</div>
        </div>
      </div>
    </div>
  );
}

const REC_ICONS = { sleep: '🌙', stress: '🧘', heart: '❤️', oxygen: '🩸', default: '💡' };
function getRecIcon(rec) {
  if (rec.toLowerCase().includes('sleep')) return REC_ICONS.sleep;
  if (rec.toLowerCase().includes('stress') || rec.toLowerCase().includes('meditat')) return REC_ICONS.stress;
  if (rec.toLowerCase().includes('heart') || rec.toLowerCase().includes('cardio')) return REC_ICONS.heart;
  if (rec.toLowerCase().includes('oxygen') || rec.toLowerCase().includes('breath')) return REC_ICONS.oxygen;
  return REC_ICONS.default;
}

export default function Insights() {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadInsights(); }, []);

  const loadInsights = async () => {
    try {
      const res = await dashboardAPI.getInsights();
      setInsights(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
      <div className="text-center">
        <div className="relative w-24 h-24 mx-auto mb-6">
          {[0,1,2].map(i => <div key={i} className="absolute inset-0 rounded-full border-2 border-emerald-500/40 animate-ping" style={{ animationDelay: `${i*0.2}s` }}/>)}
          <div className="absolute inset-0 flex items-center justify-center text-4xl">💡</div>
        </div>
        <p className="text-slate-400 text-lg font-medium">Loading Insights...</p>
      </div>
    </div>
  );

  if (!insights || insights.message) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
      <div className="text-center bg-slate-800/60 border border-slate-700 rounded-3xl p-12">
        <div className="text-7xl mb-4">💡</div>
        <h2 className="text-2xl font-bold text-white mb-2">No Insights Yet</h2>
        <p className="text-slate-400">Make predictions to generate health insights</p>
        <a href="/prediction" className="mt-6 inline-block bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-8 py-3 rounded-xl font-semibold hover:scale-105 transition-transform">Go to Prediction →</a>
      </div>
    </div>
  );

  const { health_index, behavior_patterns, recommendations, burnout_alert } = insights;
  const recovery = health_index.recovery_score;
  const recoveryColor = recovery >= 80 ? '#10b981' : recovery >= 60 ? '#3b82f6' : recovery >= 40 ? '#f59e0b' : '#ef4444';
  const recoveryLabel = recovery >= 80 ? 'Excellent' : recovery >= 60 ? 'Good' : recovery >= 40 ? 'Fair' : 'Poor';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <div className="text-center py-10 px-4">
        <div className="inline-flex items-center gap-3 bg-slate-800/60 border border-slate-700 rounded-2xl px-6 py-3 mb-4">
          <span className="text-2xl">💡</span>
          <span className="text-sm font-semibold text-slate-300 tracking-widest uppercase">AI Health Intelligence</span>
        </div>
        <h1 className="text-5xl font-black bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent mb-3">Insights</h1>
        <p className="text-slate-400 text-lg">Personalized AI wellness analysis & recommendations</p>
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-16 space-y-6">

        {/* Burnout Alert */}
        {burnout_alert && (
          <div className="relative overflow-hidden bg-red-500/10 border-2 border-red-500/60 rounded-3xl p-6">
            <div className="absolute inset-0 bg-gradient-to-r from-red-500/5 to-transparent"/>
            <div className="relative flex items-center gap-5">
              <div className="text-5xl animate-bounce">🚨</div>
              <div>
                <div className="text-2xl font-black text-red-400 mb-1">BURNOUT ALERT</div>
                <div className="text-red-300/80">Sleep under 5 hours for 3 consecutive days. Immediate rest is critical for recovery.</div>
              </div>
              <div className="ml-auto hidden md:block">
                <div className="text-6xl font-black text-red-500/20">!</div>
              </div>
            </div>
          </div>
        )}

        {/* Wellness Summary Banner */}
        <div className="relative overflow-hidden rounded-3xl p-8" style={{ background: `linear-gradient(135deg, ${recoveryColor}20, ${recoveryColor}08)`, borderColor: `${recoveryColor}40`, border: '1px solid' }}>
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-5" style={{ background: recoveryColor, transform: 'translate(30%, -30%)' }}/>
          <div className="relative flex items-center justify-between flex-wrap gap-6">
            <div>
              <div className="text-slate-400 text-sm uppercase tracking-widest font-semibold mb-2">Overall Wellness Score</div>
              <div className="text-7xl font-black" style={{ color: recoveryColor }}>{Math.round(recovery)}</div>
              <div className="text-2xl font-bold text-white mt-1">{recoveryLabel} Health Status</div>
              <div className="text-slate-400 mt-2 max-w-md">
                {recovery >= 80 && 'Outstanding! Your sleep and stress metrics are in excellent shape. Keep it up!'}
                {recovery >= 60 && recovery < 80 && 'Good progress. A few improvements in sleep consistency will boost your score.'}
                {recovery >= 40 && recovery < 60 && 'Moderate wellness. Focus on sleep duration and stress reduction techniques.'}
                {recovery < 40 && 'Attention needed. Prioritize rest, reduce stressors, and consider professional guidance.'}
              </div>
            </div>
            <div className="flex gap-2">
              {[20,40,60,80,100].map((mark, i) => (
                <div key={i} className="flex flex-col items-center gap-1">
                  <div className="w-3 rounded-full transition-all duration-1000" style={{ height: `${mark * 0.8}px`, background: recovery >= mark ? recoveryColor : '#1e293b', opacity: recovery >= mark ? 1 : 0.4 }}/>
                  <div className="text-xs text-slate-600">{mark}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Score Rings */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <ScoreRing label="Stress Score"   score={health_index.stress_score}   color="#ef4444" icon="⚡" delay={0}/>
          <ScoreRing label="Sleep Score"    score={health_index.sleep_score}    color="#3b82f6" icon="🌙" delay={150}/>
          <ScoreRing label="Recovery Score" score={health_index.recovery_score} color="#10b981" icon="💚" delay={300}/>
        </div>

        {/* Behavior Patterns + Recommendations */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-slate-800/60 border border-slate-700/50 rounded-3xl p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">📊</div>
              <div>
                <h3 className="font-bold text-white">Behavior Patterns</h3>
                <p className="text-slate-400 text-xs">30-day average metrics</p>
              </div>
            </div>
            <div className="space-y-3">
              <PatternCard icon="🌙" label="Avg Sleep Duration" value={behavior_patterns.avg_sleep_hours} unit="hours/night" color="#3b82f6" desc="Recommended: 7–9 hrs" delay={0}/>
              <PatternCard icon="❤️" label="Avg Heart Rate"     value={behavior_patterns.avg_heart_rate}  unit="bpm"         color="#ef4444" desc="Healthy: 60–80 bpm"  delay={100}/>
              <PatternCard icon="⚡" label="Avg Stress Level"   value={behavior_patterns.avg_stress_level.toFixed(1)} unit="/100" color="#f59e0b" desc="Lower is better"   delay={200}/>
            </div>
          </div>

          <div className="bg-slate-800/60 border border-slate-700/50 rounded-3xl p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">💡</div>
              <div>
                <h3 className="font-bold text-white">AI Recommendations</h3>
                <p className="text-slate-400 text-xs">Personalized action plan</p>
              </div>
            </div>
            <div className="space-y-3">
              {recommendations.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <div className="text-4xl mb-2">✅</div>
                  <div>All metrics look great! Keep it up.</div>
                </div>
              ) : recommendations.map((rec, i) => (
                <div key={i} className="flex gap-3 p-4 bg-slate-700/40 border border-slate-600/30 rounded-2xl hover:border-slate-500/50 transition-colors"
                  style={{ animationDelay: `${i * 100}ms` }}>
                  <span className="text-xl flex-shrink-0">{getRecIcon(rec)}</span>
                  <p className="text-slate-300 text-sm leading-relaxed">{rec}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Health Tips */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: '🧘', title: 'Meditation', desc: '10 min daily reduces cortisol by 20%', color: '#8b5cf6' },
            { icon: '🏃', title: 'Exercise',   desc: '30 min cardio improves sleep quality', color: '#3b82f6' },
            { icon: '📵', title: 'Screen Time', desc: 'No screens 1hr before bed', color: '#f59e0b' },
            { icon: '💧', title: 'Hydration',  desc: '8 glasses/day supports recovery', color: '#06b6d4' },
          ].map((tip, i) => (
            <div key={i} className="bg-slate-800/60 border rounded-2xl p-5 hover:scale-105 transition-transform cursor-default"
              style={{ borderColor: `${tip.color}30` }}>
              <div className="text-3xl mb-3">{tip.icon}</div>
              <div className="font-bold text-white text-sm mb-1">{tip.title}</div>
              <div className="text-xs text-slate-500">{tip.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
